import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs'; // [cite: 8]
import { useToast } from '../../../hooks/use-toast'; // [cite: 27]
import EditSubTab from './Qna/EditSubTab';
import QnAFlagsSetup from './Qna/QnAFlags'
import PublishView from './Qna/PublishView';
import ABTestingTab from './Qna/ABTestingTab';
import api from '../../../services/apiServices';
import sortQuestionOptions from './Qna/SortQna'
//import { useAdminStore } from '../../../store/adminStore'; // <-- NEW IMPORT

export default function EditQnA({ category, locale }) {
  const [questions, setQuestions] = useState([]);
  const [activeSubTab, setActiveSubTab] = useState('edit'); // Default tab
  const [showInactive, setShowInactive] = useState(false);
  const { toast } = useToast(); // [cite: 27]
  //const locale = useAdminStore((state) => state.locale); // <-- GET CONTEXT

  const categoryId = category?.id;

  const fetchQuestions = useCallback(async () => {
    if (!categoryId) {
      setQuestions([]);
      return;
    }

    try {
      const response = await api.get(`/admin/categories/cat-qna/category/${categoryId}`,{
        params: { locale, include_inactive: showInactive },
      });
      const data = response.data;

      console.log("Fetched questions:", data);
      const processedData = data
        .map(sortQuestionOptions) // <-- THIS IS THE FIX
        .map((q) => ({ ...q, isNew: false, isEdited: false }));

      setQuestions(processedData);
      console.log("Processed questions:", processedData);
    } catch (error) {
      console.error("Error fetching questions:", error);

      const message =
        error?.response?.data?.message || error.message || "An unexpected error occurred. Please try again.";

      toast({
        variant: "destructive",
        title: "Failed to Load Questions",
        description: message,
      });

      setQuestions([]); // Clear questions on error
    }
  }, [categoryId, toast, showInactive]);

  // Toggling "Show inactive" triggers a full refetch (via the effect below),
  // which replaces `questions` wholesale — guard against silently discarding
  // any unsaved local edits (new/edited/deleted/reordered rows).
  const handleToggleShowInactive = useCallback((next) => {
    const hasUnsavedChanges = questions.some(
      (q) => q.isNew || q.isEdited || q.is_deleted || q.order_index_changed
    );
    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        "You have unsaved changes that will be lost if you switch this view. Continue?"
      );
      if (!confirmed) return;
    }
    setShowInactive(next);
  }, [questions]);

  // Question/option flag rules (the "Category Q&A Flags" tab) must only ever
  // be built against live data, regardless of whether "Show inactive" is on
  // in the Edit tab.
  const liveQuestionsForFlags = useMemo(() => {
    return questions
      .filter((q) => !q.is_deleted && (q.isNew || (q.status ?? 'draft') === 'draft'))
      .map((q) => ({
        ...q,
        options: (q.options || []).filter((opt) => opt.is_active !== false),
      }));
  }, [questions]);

  useEffect(() => {
    // Ensure categoryId is present before fetching.
    // If categoryId can be initially undefined and then defined, this effect will trigger when it gets defined.
    if (categoryId) {
      fetchQuestions();
    } else {
      // Handle the case where category is not available on mount
      // You might want to clear questions or show a specific UI state
      setQuestions([]);
      // Optionally, if a category is *expected* but not present, you could show a toast.
      // For example, if `category` is usually loaded asynchronously itself.
      // toast({
      //   variant: "default",
      //   title: "Information",
      //   description: "Please select a category to see questions.",
      // });
    }
  }, [categoryId, fetchQuestions]); // fetchQuestions is now a dependency

  // Default to 'edit' if categoryId is not present, or handle as per your UX requirements
  // This ensures the Tabs component has a valid defaultValue.
  const currentTab = categoryId ? activeSubTab : 'edit';

  return (
    <div className="w-full">
      <Tabs value={currentTab} onValueChange={setActiveSubTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto gap-1 md:max-w-2xl mb-4">
          <TabsTrigger value="edit" className="whitespace-normal h-auto py-2 text-center">Edit</TabsTrigger>
          <TabsTrigger value="qnaFlags" disabled={!categoryId} className="whitespace-normal h-auto py-2 text-center">Category Q&A Flags</TabsTrigger>
          <TabsTrigger value="abTesting" disabled={!categoryId} className="whitespace-normal h-auto py-2 text-center">A/B Testing</TabsTrigger>
          <TabsTrigger value="publish" disabled={!categoryId} className="whitespace-normal h-auto py-2 text-center">Publish</TabsTrigger>
        </TabsList>

        <TabsContent value="edit">
          {categoryId ? (
            <EditSubTab
              questions={questions}
              setQuestions={setQuestions}
              categoryId={categoryId}
              fetchQuestions={fetchQuestions}
              locale={locale}
              showInactive={showInactive}
              onToggleShowInactive={handleToggleShowInactive}
            />
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              Please select or define a category to manage questions.
            </div>
          )}
        </TabsContent>
        <TabsContent value="qnaFlags">
          {categoryId ? (
            <QnAFlagsSetup
              category={category}
              questions={liveQuestionsForFlags}
              setQuestions={setQuestions}
              fetchQuestions={fetchQuestions}
              locale={locale}
            />
          ) : (
             <div className="text-center py-10 text-muted-foreground">
              Condition setup requires an active category.
            </div>
          )}
        </TabsContent>
        <TabsContent value="abTesting">
          {categoryId ? (
            <ABTestingTab categoryId={categoryId} locale={locale} />
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              A/B testing requires an active category.
            </div>
          )}
        </TabsContent>
        <TabsContent value="publish">
          {categoryId ? (
            <PublishView categoryId={categoryId} fetchQuestions={fetchQuestions} />
          ) : (
            <div className="text-center py-10 text-muted-foreground">
              Publishing requires an active category.
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}